/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React, { useCallback, useState } from 'react';
import { presentUnexpectedFailure } from '../../../shared/sentry/presentUnexpectedFailure';
import {
  ActivityIndicator,
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  Share,
  View,
} from 'react-native';
import * as Print from 'expo-print';
import * as Sharing from 'expo-sharing';
import { Ionicons } from '@expo/vector-icons';
import { printOrderComandaSafe } from '../../../printing/application/printComandaDocuments';
import { ticketColors, ticketFonts } from '../../../shared/theme/ticketColors';
import {
  formatOrderDisplayCodeLabel,
  type CustomerOrderDetail,
  type OrderStatus,
} from '../../domain/orderTypes';
import { showError, showSuccess } from '../../../shared/ui/appToast';
import { buildOrderComanda } from './buildOrderComanda';

const COMANDA_STATUSES: OrderStatus[] = ['confirmed', 'preparing', 'ready'];

type Props = { order: CustomerOrderDetail };

/** Imprimir / compartir etiqueta-comanda con display_code (sin PIN). */
export function OrderComandaShareButton({ order }: Props) {
  const [busy, setBusy] = useState<'print' | 'share' | null>(null);

  const print = useCallback(async () => {
    setBusy('print');
    try {
      await printOrderComandaSafe({
        branchId: order.branch_id,
        order,
        onError: showError,
      });
    } finally {
      setBusy(null);
    }
  }, [order]);

  const share = useCallback(async () => {
    setBusy('share');
    try {
      const { text, html } = buildOrderComanda(order);
      const title = `Comanda ${formatOrderDisplayCodeLabel(order.display_code, order.id)}`;
      if (Platform.OS === 'web') {
        if (typeof navigator !== 'undefined' && navigator.share) {
          await navigator.share({ title, text });
        } else if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(text);
          showSuccess('Comanda', 'Copiada');
        } else {
          await Share.share({ message: text, title });
        }
        return;
      }
      const canShare = await Sharing.isAvailableAsync();
      if (canShare) {
        const { uri } = await Print.printToFileAsync({ html });
        await Sharing.shareAsync(uri, { mimeType: 'application/pdf', dialogTitle: title });
      } else {
        await Share.share({ message: text, title });
      }
    } catch (e: any) {
      if (e?.message && !String(e.message).includes('Share canceled')) {
        presentUnexpectedFailure({ module: 'orders', op: 'comanda', error: e, title: 'Comanda', userMessage: 'No se pudo compartir' });
      }
    } finally {
      setBusy(null);
    }
  }, [order]);

  if (!COMANDA_STATUSES.includes(order.status)) return null;

  return (
    <View style={styles.wrap}>
      <Text style={styles.hint}>Imprimí y pegá / meté en la bolsa</Text>
      <View style={styles.row}>
        <TouchableOpacity
          style={styles.btn}
          onPress={() => void print()}
          disabled={busy != null}
          accessibilityRole="button"
          accessibilityLabel="Imprimir comanda"
        >
          {busy === 'print' ? (
            <ActivityIndicator color={ticketColors.gold} />
          ) : (
            <Ionicons name="print-outline" size={18} color={ticketColors.gold} />
          )}
          <Text style={styles.label}>Imprimir</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.btn}
          onPress={() => void share()}
          disabled={busy != null}
          accessibilityRole="button"
          accessibilityLabel="Compartir comanda"
        >
          {busy === 'share' ? (
            <ActivityIndicator color={ticketColors.gold} />
          ) : (
            <Ionicons name="share-outline" size={18} color={ticketColors.gold} />
          )}
          <Text style={styles.label}>Compartir</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: { gap: 6 },
  hint: {
    fontSize: 12,
    fontWeight: '600',
    color: ticketColors.ink3,
    fontFamily: ticketFonts.sans,
  },
  row: { flexDirection: 'row', gap: 8, flexWrap: 'wrap' },
  btn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
  },
  label: {
    fontSize: 13,
    fontWeight: '700',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
});
